import { usePage } from "@inertiajs/react"

import type { SharedProps } from "@/types"

/**
 * OKS-984 — reads the `pending_shipments_count` shared prop emitted by
 * `ApplicationController#inertia_share`.
 *
 * Counts **Ready to ship only** (PRD OQ-5): a badge including rows the
 * operator cannot act on never reaches zero and gets ignored within a week.
 *
 * ⚠️ The key is **absent** for anyone who is not a brand-side operator —
 * creators and admins without a brand context. Returns 0 in that case, which
 * the sidebar renders as no chip at all. Absence and zero are deliberately
 * indistinguishable HERE (both mean "no badge"); the distinction that matters
 * is server-side, where emitting the key to a creator would be a leak.
 *
 * ⚠️ Refreshed by the post-mutation REDIRECT, not by an explicit
 * `router.reload`. `#transitions` / `#remind` redirect, Inertia makes a fresh
 * visit, the deferred manifest is re-emitted and the batch re-fetched. An
 * earlier version of this comment named a `router.reload` call that exists
 * nowhere in the codebase.
 *
 * ⚠️ Consequence: the badge's freshness is a property of the VISIT. A
 * `preserveState: true` on the mark-shipped dialog, or any close path that
 * skips the visit, would stale it silently — nothing would fail.
 */
export function usePendingShipmentsCount(): number {
  const page = usePage<Partial<SharedProps>>()
  const value = page.props.pending_shipments_count

  if (typeof value !== "number" || Number.isNaN(value)) return 0
  return value
}
